import { auth } from '@/lib/firebase-client'
import { onAuthStateChanged } from 'firebase/auth'

// Wait for Firebase Auth to be ready and return current user's token
function getToken(): Promise<string | null> {
  return new Promise((resolve) => {
    // If user is already available, return token immediately
    if (auth.currentUser) {
      auth.currentUser.getIdToken().then(resolve).catch(() => resolve(null))
      return
    }
    // Otherwise wait for auth state to initialize
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      unsubscribe()
      if (user) {
        try {
          const token = await user.getIdToken()
          resolve(token)
        } catch {
          resolve(null)
        }
      } else {
        resolve(null)
      }
    })
  })
}

export async function apiFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await getToken()

  let baseUrl = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/$/, '')
  // Fallback to local proxy in development if pointing to a remote URL
  if (process.env.NODE_ENV === 'development' && baseUrl && !baseUrl.includes('localhost') && !baseUrl.includes('127.0.0.1')) {
    baseUrl = ''
  }
  // In production the frontend is served as static files with no dev proxy, so
  // every /api request MUST go to the backend URL. If NEXT_PUBLIC_API_URL wasn't
  // set at build time, requests fall through to the SPA's index.html and return
  // HTML ("API server returned HTML instead of JSON") — every checkout/break
  // silently fails. Fail loudly here instead.
  if (!baseUrl && process.env.NODE_ENV === 'production' && !url.startsWith('http') && url.startsWith('/api')) {
    throw new Error('NEXT_PUBLIC_API_URL is not set. The frontend cannot reach the backend API.')
  }
  const cleanUrl = url.startsWith('/') ? url : `/${url}`
  const fullUrl = url.startsWith('http') ? url : `${baseUrl}${cleanUrl}`

  const response = await fetch(fullUrl, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  })

  // Fail early if the server returned HTML (e.g. Firebase Hosting fallback index.html)
  const contentType = response.headers.get('content-type') || ''
  if (contentType.includes('text/html')) {
    throw new Error(`API server returned HTML instead of JSON. (Requested URL: ${fullUrl}). Ensure NEXT_PUBLIC_API_URL is set to your running backend server.`)
  }

  // Check for network errors
  if (!response.ok) {
    // Try to get error message from response body
    let errorMessage = `HTTP error! status: ${response.status}`
    try {
      const errorData = await response.json() as any
      if (errorData.error) {
        errorMessage = errorData.error
      }
    } catch {
      // Response body might not be JSON
    }
    
    const error = new Error(errorMessage)
    ;(error as any).status = response.status
    throw error
  }

  return response
}

/**
 * API fetch utility with retry mechanism
 */
interface RetryOptions {
  maxRetries?: number
  initialDelay?: number
  maxDelay?: number
  backoffFactor?: number
}

export async function apiFetchWithRetry(
  url: string,
  options: RequestInit = {},
  retryOptions: RetryOptions = {}
): Promise<Response> {
  const {
    maxRetries = 3,
    initialDelay = 1000,
    maxDelay = 5000,
    backoffFactor = 2
  } = retryOptions

  let lastError: Error | undefined

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await apiFetch(url, options)
    } catch (error) {
      lastError = error as Error

      // Don't retry on auth failures or client errors. Check the `status`
      // property attached in apiFetch — substring matching on error.message
      // false-positived on legitimate validation errors mentioning "Unauthorized".
      const status = (error as any)?.status
      if (typeof status === 'number' && status >= 400 && status < 500) {
        break
      }

      // Don't retry on the last attempt
      if (attempt === maxRetries - 1) {
        break
      }

      // Calculate delay with exponential backoff
      const delay = Math.min(
        initialDelay * Math.pow(backoffFactor, attempt),
        maxDelay
      )

      console.warn(`API request failed (attempt ${attempt + 1}/${maxRetries}), retrying in ${delay}ms...`, error)
      
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }

  throw lastError || new Error('API request failed after retries')
}
